import type { GuestDataProps } from "@/components/GuestForm";
import { environment } from "@/config/env";
import type { GuestState } from "@/pages/CheckIn";
import axios from "axios";
import nProgress from "nprogress";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export interface EliteReservationContextType {
  reservation: any | null;
  fromKiosk?: boolean;
  isLoading?: boolean;
  setReservation: (
    value: any | null,
    fromKiosk?: boolean,
    token?: string,
  ) => void;
  checkIn: (props: GuestState, visitReason: number) => Promise<any>;
  refreshReservation: () => Promise<any>;
}
export const EliteReservationContext = createContext<
  EliteReservationContextType | undefined
>(undefined);

export const useEliteReservation = (): EliteReservationContextType => {
  const context = useContext(EliteReservationContext);
  if (context === undefined) {
    throw new Error(
      "EliteReservationContext must be used inside a valid context",
    );
  }
  return context;
};

export interface EliteStructureContextType {
  structure: any | null;
  labelTranslationService: any | null;
  setStructureData: (value: any | null) => void;
  setLabelTranslationService: (value: any | null) => void;
  getStructure: (structureId: string) => Promise<void>;
}

export const EliteStructureContext = createContext<
  EliteStructureContextType | undefined
>(undefined);

export const useEliteStructure = (): EliteStructureContextType => {
  const context = useContext(EliteStructureContext);
  if (context === undefined) {
    throw new Error(
      "EliteStructureContext must be used inside a valid context",
    );
  }
  return context;
};

export function EliteReservationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [reservation, setReservation] = useState<any | null>(null);
  const [fromKiosk, setFromKiosk] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSetReservation = (
    value: any | null,
    fromKioskParam?: boolean,
    token?: string,
  ) => {
    setReservation(value);
    if (fromKioskParam !== undefined) {
      setFromKiosk(fromKioskParam);
    }
    if (token) {
      sessionStorage.setItem("webcheckin_token", token);
    }
  };

  const refreshReservation = async (): Promise<any> => {
    const token = sessionStorage.getItem("webcheckin_token");
    if (!token) return null;

    axios.defaults.baseURL = environment.ELITE_BASE_URI;
    axios.defaults.headers.common["x-auth-key"] = token;
    try {
      setIsLoading(true);
      const res = await axios.get("webcheckin/data");
      const { reservationData } = res.data;
      setReservation(reservationData);
      return reservationData;
    } catch (err) {
      console.warn("Unable to refresh reservation data", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem("webcheckin_token");
    if (token && !reservation) {
      axios.defaults.baseURL = environment.ELITE_BASE_URI;
      axios.defaults.headers.common["x-auth-key"] = token;
      axios
        .get("webcheckin/data")
        .then((response) => {
          const { reservationData } = response.data;
          setReservation(reservationData);
        })
        .catch(() => {
          sessionStorage.removeItem("webcheckin_token");
        });
    }
  }, []);

  const checkIn = (guestData: GuestState, visitReason: number) => {
    return new Promise<any>((resolve, reject) => {
      let guests = [] as GuestDataProps[];
      nProgress.start();
      // Semplifichiamo il ciclo per estrarre i dati
      Object.keys(guestData).forEach((k) => {
        const guestDataProps = guestData[k as any]; // Usa la chiave k correttamente
        if (Array.isArray(guestDataProps)) {
          guests.push(...guestDataProps);
        }
      });

      // Mappiamo i dati per includere i formati corretti per il backend
      const mappedGuests = guests.map((guest) => {
        const issuingCountry = guest.issuingCountry?.iso2
          ? {
              id: guest.issuingCountry.id || 0,
              iso2: guest.issuingCountry.iso2,
              name:
                guest.issuingCountry.name?.trim() ||
                (guest.issuingCountry.iso2.toUpperCase() === "IT"
                  ? "Italia"
                  : guest.issuingCountry.iso2),
            }
          : undefined;

        const birthCountry = guest.birthCountry?.iso2
          ? {
              id: guest.birthCountry.id || 0,
              iso2: guest.birthCountry.iso2,
              name:
                guest.birthCountry.name?.trim() ||
                (guest.birthCountry.iso2.toUpperCase() === "IT"
                  ? "Italia"
                  : guest.birthCountry.iso2),
            }
          : undefined;

        const residenceCountry = guest.residenceCountry?.iso2
          ? {
              id: guest.residenceCountry.id || 0,
              iso2: guest.residenceCountry.iso2,
              name:
                guest.residenceCountry.name?.trim() ||
                (guest.residenceCountry.iso2.toUpperCase() === "IT"
                  ? "Italia"
                  : guest.residenceCountry.iso2),
            }
          : undefined;

        return {
          ...guest,
          issuingCountry,
          birthCountry,
          residenceCountry,
          issuingCity: guest.issuingCity
            ? {
                id: Number(guest.issuingCity.id) || 0,
                name: guest.issuingCity.name,
              }
            : undefined,
        };
      });

      axios
        .post(
          `${environment.ELITE_BASE_URI}/webcheckin/checkin`,
          {
            guests: mappedGuests,
            visitReason: visitReason,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        )
        .then(({ data }) => {
          setReservation(data.reservationData);
          resolve(data.reservationData);
        })
        .catch((error) => {
          reject(error);
        })
        .finally(() => {
          nProgress.done();
        });
    });
  };

  const contextValue: EliteReservationContextType = {
    reservation,
    setReservation: handleSetReservation,
    fromKiosk,
    isLoading,
    checkIn,
    refreshReservation,
  };

  return (
    <EliteReservationContext.Provider value={contextValue}>
      {children}
    </EliteReservationContext.Provider>
  );
}

export function EliteStructureContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [structureId, setStructureId] = useState<number>(() =>
    Number(localStorage.getItem(`structureId`)),
  );

  const [structure, setStructure] = useState<any | null>();
  const [labelTranslationService, setLabelTranslationService] = useState<
    any | null
  >(null);

  const setStructureData = (value: any | null) => {
    localStorage.setItem("structureId", value.id);

    setStructureId(value.id);
    setStructure(value);
  };

  const getStructure = (structureId: string): Promise<void> => {
    axios.defaults.baseURL = environment.ELITE_BASE_URI;

    return axios
      .get(`webcheckin/structure/${structureId}`)
      .then((response) => {
        const { structureContextData, labelTranslationService } = response.data;
        setStructure(structureContextData);
        setLabelTranslationService(labelTranslationService);
      })
      .catch(() => {
        console.warn("Unable to load structure data");
        // TODO: redirect to 404
      });
  };

  useEffect(() => {
    if (
      (structureId && !structure) ||
      (structureId && structure.id != structureId) ||
      (structureId && !labelTranslationService)
    ) {
      getStructure(structureId.toString());
    }
  }, [structure?.id, structureId]);

  const contextValue: EliteStructureContextType = {
    structure,
    labelTranslationService,
    setStructureData,
    setLabelTranslationService,
    getStructure,
  };

  return (
    <EliteStructureContext.Provider value={contextValue}>
      {children}
    </EliteStructureContext.Provider>
  );
}
